import type { ExportConfig, GeneratedFile, LanguageGenerator } from './types';
import { generateEnv, toPascal, toCamel } from './utils';

function csType(t: string): string {
  return t === 'number' ? 'double' : t === 'boolean' ? 'bool' : t === 'array' ? 'List<object>' : t === 'object' ? 'Dictionary<string, object>' : 'string';
}

function genConfig(config: ExportConfig): string {
  const appEntries = config.apps
    .map((a) => `        [\"${a.key}\"] = new AppConfig("${a.fromEmail}", "${a.envVarName}")`)
    .join(",\n");

  const templateEntries = config.apps
    .flatMap((a) =>
      a.templates.map((t) => `        [\"${t.camelName}\"] = new TemplateConfig("${a.key}", "${t.slug}")`)
    )
    .join(",\n");

  return `// MailConfig.cs — generated by Mail Service SDK Export
using System.Collections.Generic;

public record AppConfig(string FromEmail, string KeyEnv);
public record TemplateConfig(string App, string Slug);

public static class MailConfig
{
    public static readonly Dictionary<string, AppConfig> Apps = new()
    {
${appEntries},
    };

    public static readonly Dictionary<string, TemplateConfig> Templates = new()
    {
${templateEntries},
    };
}
`;
}

function genService(config: ExportConfig): string {
  const typedMethods = config.apps
    .flatMap((a) =>
      a.templates.map((t) => {
        const methodName = `Send${toPascal(t.camelName)}Async`;
        if (t.schema && t.schema.length > 0) {
          const params = t.schema
            .map((f) => `${csType(f.type)} ${toCamel(f.key)}`)
            .join(', ');
          const dictEntries = t.schema
            .map((f) => `{ "${f.key}", ${toCamel(f.key)} }`)
            .join(', ');
          return `    public Task<Dictionary<string, object>> ${methodName}(string recipient, ${params})\n        => SendAsync("${t.camelName}", recipient, new() { ${dictEntries} });`;
        }
        return `    public Task<Dictionary<string, object>> ${methodName}(string recipient, Dictionary<string, object>? data = null)\n        => SendAsync("${t.camelName}", recipient, data);`;
      }),
    )
    .join("\n\n");

  return `// MailService.cs — generated by Mail Service SDK Export
using System;
using System.Collections.Generic;
using System.Net.Http;
using System.Net.Http.Json;
using System.Threading.Tasks;

public class MailService
{
    private static readonly HttpClient Http = new();
    private readonly string _serviceUrl;

    public MailService()
    {
        _serviceUrl = (Environment.GetEnvironmentVariable("${config.serviceUrlVarName}") ?? "").TrimEnd('/');
    }

    private string GetKey(string app)
        => Environment.GetEnvironmentVariable(MailConfig.Apps[app].KeyEnv) ?? "";

    private bool IsReady(string app) => !string.IsNullOrEmpty(_serviceUrl) && !string.IsNullOrEmpty(GetKey(app));

    private async Task<Dictionary<string, object>> PostAsync(string app, string path, object body)
    {
        if (!IsReady(app)) return new() { ["success"] = false, ["error"] = "not configured" };
        var req = new HttpRequestMessage(HttpMethod.Post, _serviceUrl + path);
        req.Headers.Add("X-API-KEY", GetKey(app));
        req.Content = JsonContent.Create(body);
        var resp = await Http.SendAsync(req);
        return await resp.Content.ReadFromJsonAsync<Dictionary<string, object>>()
            ?? new() { ["success"] = false };
    }

    public Task<Dictionary<string, object>> SendAsync(string template, string recipient, Dictionary<string, object>? data = null)
    {
        var t = MailConfig.Templates[template];
        return PostAsync(t.App, "/v1/send", new {
            template_slug = t.Slug, recipient, data = data ?? new()
        });
    }

${typedMethods}
}
`;
}

export const csharpGenerator: LanguageGenerator = {
  id: 'csharp',
  name: 'C#',
  category: 'full-sdk',
  color: '#512BD4',
  generate(config: ExportConfig): GeneratedFile[] {
    return [
      { filename: 'MailConfig.cs', content: genConfig(config), syntax: 'csharp' },
      { filename: 'MailService.cs', content: genService(config), syntax: 'csharp' },
      { filename: '.env', content: generateEnv(config), syntax: 'shell' },
    ];
  },
};
